import { formatDistanceToNow } from 'date-fns';
import { type TaskExecution, useTaskExecutions } from '@/hooks/useTaskExecutions';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { cn } from '@/lib/utils';

const RESULT_STYLE: Record<string, string> = {
  alert: 'bg-sev-1/15 text-sev-1',
  error: 'bg-sev-3/20 text-foreground',
  ok: 'bg-sev-4/15 text-sev-4',
};

const COLUMNS: readonly ResponsiveTableColumn<TaskExecution>[] = [
  {
    key: 'time',
    label: 'Time',
    render: (e) => (
      <span className="text-xs">
        {formatDistanceToNow(new Date(e.timestamp), { addSuffix: true })}
      </span>
    ),
    hideMobileLabel: true,
  },
  {
    key: 'result',
    label: 'Result',
    render: (e) =>
      e.result ? (
        <Badge
          className={cn(
            'border-0 font-normal',
            RESULT_STYLE[e.result] ?? 'bg-muted text-muted-foreground',
          )}
        >
          {e.result}
        </Badge>
      ) : null,
  },
  {
    key: 'duration',
    label: 'Duration',
    render: (e) => <span className="font-mono text-xs">{e.durationMs}ms</span>,
    align: 'right',
    hideAt: 'mobile',
  },
  {
    key: 'evaluated',
    label: 'Evaluated',
    render: (e) => <span className="font-mono text-xs">{e.targetsEvaluated}</span>,
    align: 'right',
    hideAt: 'mobile',
  },
  {
    key: 'alerting',
    label: 'Alerting',
    render: (e) => (
      <span
        className={cn(
          'font-mono text-xs',
          e.targetsAlerting > 0 && 'text-sev-1 font-medium',
        )}
      >
        {e.targetsAlerting}
      </span>
    ),
    align: 'right',
  },
  {
    key: 'cleared',
    label: 'Cleared',
    render: (e) => <span className="font-mono text-xs">{e.targetsCleared}</span>,
    align: 'right',
    hideAt: 'mobile',
  },
];

export function ExecutionHistory({ taskId, limit = 20 }: { taskId: string; limit?: number }) {
  const { data, isLoading } = useTaskExecutions(taskId, { limit });

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (!data || data.executions.length === 0) {
    return <p className="text-sm text-muted-foreground">No execution history available.</p>;
  }

  return (
    <ResponsiveTable<TaskExecution>
      columns={COLUMNS}
      rows={data.executions}
      rowKey={(r) => r.eventId}
    />
  );
}
